import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DemandRow {
  readonly demandId: string;
  readonly demandCode: string;
  readonly title: string;
  readonly demandType: string;
  readonly priority: string;
  readonly status: string;
  readonly requestedBy: string | null;
}

const TYPES = [
  '', 'ENHANCEMENT', 'NEW_FEATURE', 'COMPLIANCE_CHANGE', 'PROCESS_IMPROVEMENT',
  'AUTOMATION', 'INNOVATION', 'STRATEGIC_INITIATIVE', 'TECHNICAL_IMPROVEMENT',
];
const STATUSES = [
  '', 'DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'BUSINESS_ASSESSMENT', 'TECHNICAL_ASSESSMENT',
  'PRIORITIZED', 'APPROVED', 'PLANNED', 'IMPLEMENTED', 'CLOSED', 'REJECTED', 'DEFERRED',
];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Demand Intake Portal (§31 / §6-§7). Centralised register of demands
 * (T433) captured through intake, carrying demand type, priority and the
 * twelve-state governance lifecycle. Supports intaking a demand and driving it
 * through the lifecycle — submit, review, business/technical assessment,
 * prioritize, approve, plan, implement, close, reject and defer — each wired to
 * the guarded /v1/pm/demands write API. Filterable by demand type and status.
 */
export function DemandIntakePortal(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DemandRow[]>([]);
  const [demandType, setDemandType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('ENHANCEMENT');
  const [newPriority, setNewPriority] = useState('MEDIUM');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (demandType) params.set('demandType', demandType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DemandRow[] }>(`/v1/pm/demands${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, demandType, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.demand.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/demands`, {
        method: 'POST',
        body: { title: newTitle.trim(), demandType: newType, priority: newPriority },
        token,
      });
      setNotice(t('m33.demand.intaken', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'DEMAND_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (
    r: DemandRow,
    path: string,
    label: string,
    perm: string,
    body?: Record<string, unknown>,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/demands/${r.demandId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.demandCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const setInput = (id: string, value: string): void => setInputs((m) => ({ ...m, [id]: value }));

  return (
    <main aria-label="demand-intake-portal">
      <h1>{t('m33.demand.title')}</h1>

      <section aria-label="intake-demand">
        <h2>{t('m33.demand.intakeHeading')}</h2>
        <form aria-label="demand-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-demand-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-demand-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m33.common.priority')}
            <select aria-label="field-demand-priority" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-demand" disabled={busy}>{busy ? t('m33.demand.intaking') : t('m33.demand.intakeBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="demand-error">{actionError}</p>}
        {notice && <p aria-label="demand-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-demand-type" value={demandType} onChange={(e) => setDemandType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="demand-count">{t('m33.demand.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="demand-table"
            rows={rows}
            rowKey={(r) => r.demandId}
            columns={[
              { key: 'demandCode', header: t('m33.common.code'), render: (r) => r.demandCode },
              { key: 'title', header: t('m33.common.title'), render: (r) => r.title },
              { key: 'demandType', header: t('m33.common.type'), render: (r) => r.demandType },
              { key: 'priority', header: t('m33.common.priority'), render: (r) => r.priority },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.demandCode}`} onClick={() => void act(r, 'submit', t('m33.demand.act.submitted'), 'DEMAND_MANAGE')}>{t('m33.demand.btnSubmit')}</Button>
                  )}
                  {r.status === 'SUBMITTED' && (
                    <Button variant="secondary" type="button" aria-label={`review-${r.demandCode}`} onClick={() => void act(r, 'review', t('m33.demand.act.underReview'), 'DEMAND_ASSESS')}>{t('m33.demand.btnReview')}</Button>
                  )}
                  {(r.status === 'UNDER_REVIEW' || r.status === 'BUSINESS_ASSESSMENT') && (
                    <>
                      <input aria-label={`assessment-${r.demandCode}`} value={inputs[r.demandId] ?? ''} onChange={(e) => setInput(r.demandId, e.target.value)} placeholder={t('m33.demand.assessmentPh')} />
                      <Button variant="secondary" type="button" aria-label={`assess-business-${r.demandCode}`} onClick={() => void act(r, 'assess-business', t('m33.demand.act.businessAssessed'), 'DEMAND_ASSESS', { businessAssessment: inputs[r.demandId] ?? '' })}>{t('m33.demand.btnBusiness')}</Button>
                      <Button variant="secondary" type="button" aria-label={`assess-technical-${r.demandCode}`} onClick={() => void act(r, 'assess-technical', t('m33.demand.act.technicalAssessed'), 'DEMAND_ASSESS', { technicalAssessment: inputs[r.demandId] ?? '' })}>{t('m33.demand.btnTechnical')}</Button>
                    </>
                  )}
                  {r.status === 'TECHNICAL_ASSESSMENT' && (
                    <>
                      <input aria-label={`priority-score-${r.demandCode}`} type="number" value={inputs[r.demandId] ?? ''} onChange={(e) => setInput(r.demandId, e.target.value)} placeholder={t('m33.demand.scorePh')} />
                      <Button variant="secondary" type="button" aria-label={`prioritize-${r.demandCode}`} onClick={() => void act(r, 'prioritize', t('m33.demand.act.prioritized'), 'DEMAND_ASSESS', { priorityScore: Number(inputs[r.demandId] ?? 0) })}>{t('m33.demand.btnPrioritize')}</Button>
                    </>
                  )}
                  {r.status === 'PRIORITIZED' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.demandCode}`} onClick={() => void act(r, 'approve', t('m33.demand.act.approved'), 'DEMAND_APPROVE')}>{t('m33.demand.btnApprove')}</Button>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="secondary" type="button" aria-label={`plan-${r.demandCode}`} onClick={() => void act(r, 'plan', t('m33.demand.act.planned'), 'DEMAND_MANAGE')}>{t('m33.demand.btnPlan')}</Button>
                  )}
                  {r.status === 'PLANNED' && (
                    <Button variant="secondary" type="button" aria-label={`implement-${r.demandCode}`} onClick={() => void act(r, 'implement', t('m33.demand.act.implemented'), 'DEMAND_MANAGE')}>{t('m33.demand.btnImplement')}</Button>
                  )}
                  {r.status === 'IMPLEMENTED' && (
                    <Button variant="secondary" type="button" aria-label={`close-${r.demandCode}`} onClick={() => void act(r, 'close', t('m33.demand.act.closed'), 'DEMAND_MANAGE')}>{t('m33.demand.btnClose')}</Button>
                  )}
                  {!['CLOSED', 'REJECTED', 'DEFERRED', 'IMPLEMENTED'].includes(r.status) && (
                    <>
                      <input aria-label={`reject-reason-${r.demandCode}`} value={inputs[`rej-${r.demandId}`] ?? ''} onChange={(e) => setInput(`rej-${r.demandId}`, e.target.value)} placeholder={t('m33.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.demandCode}`} onClick={() => void act(r, 'reject', t('m33.demand.act.rejected'), 'DEMAND_APPROVE', { reason: inputs[`rej-${r.demandId}`] ?? '' })}>{t('m33.demand.btnReject')}</Button>
                      <Button variant="secondary" type="button" aria-label={`defer-${r.demandCode}`} onClick={() => void act(r, 'defer', t('m33.demand.act.deferred'), 'DEMAND_APPROVE', { reason: inputs[`rej-${r.demandId}`] ?? '' })}>{t('m33.demand.btnDefer')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
